import produce from 'immer';
import { get, set } from 'lodash';
import { type IBooleanCondition } from '../../../../../../inputs/expressions-editor/conditions/IBooleanCondition.tsx';
import { type IAdvancedRichText } from '../../../../../../inputs/rich-text-advanced/IAdvancedRichText.ts';
import { debug } from '../../../../../../utils/debug.ts';
import { engineUuid } from '../../../../../../utils/uuid/engineUuid.ts';
import { WidgetConditionManager } from '../../../../../../validator/WidgetConditionManager.ts';
import { type IReference } from '../../../../../expressions/references/IReference.ts';
import {
  findVirtualReferencesInWidget,
  type IVirtualReferencePaths,
} from '../../../../../expressions/references/virtual/findVirtualReferencesInWidget.ts';
import { normalizeVirtualReference } from '../../../../../expressions/references/virtual/normalizeVirtualReference.ts';
import { normalizeVirtualRefsRte } from '../../../../../expressions/references/virtual/normalizeVirtualRefsRte.ts';
import { type IBaseWidget } from '../../../../IBaseWidget.ts';
import { type ICodeWidget } from '../../../../ICodeWidget.ts';
import { type Widget } from '../../../../Widget.ts';
import { TableCellType } from '../ITableCell.ts';
import {
  type ITableColumnWidget,
  type ITableInputWidget,
} from '../ITableInputWidget.ts';
import { materializeTableWidgetRowPrefix } from './materializeTableWidgetRowPrefix.ts';

export function engineWidgetCellId(
  table: ITableInputWidget,
  rowId: string,
  columnId: string,
) {
  return engineUuid(`${table.id}.${rowId}.${columnId}`);
}

interface IMergeable {
  visible?: IBooleanCondition | undefined;
  enabled?: IBooleanCondition | undefined;
}

function mergeConditions(column: IMergeable, row: IMergeable) {
  return {
    visible: WidgetConditionManager.merge([column.visible, row.visible])
      ?.condition,
    enabled: WidgetConditionManager.merge([column.enabled, row.enabled])
      ?.condition,
  };
}

export function materializeTableInputWidgets(
  table: ITableInputWidget,
): Widget[] {
  const widgets: Widget[] = [];

  // Mappo le colonne per velocizzare la ricerca durante l'iterazione delle righe
  const columnMap = new Map<string, ITableColumnWidget>();
  table.columns.forEach(col => {
    columnMap.set(col.id, col);
  });

  for (let r = 0; r < table.rows.length; r++) {
    const row = table.rows[r];

    if (row.customRow) {
      widgets.push(
        // Ritorniamo i widget della custom row così come sono configurati.
        // In caso cambiassimo idea e volessimo materializzarli, ricordiamoci
        // di aggiornare anche il `getTableWidgetAtCell`
        ...row.cells.flatMap(cell => {
          if (cell.type === TableCellType.Empty) return [];
          if (!cell.columnId) return [];

          // Prendiamo la colonna della cella per poter aggiornare correttamente
          // le regole di visibilità/abilitazione. Le celle non hanno una
          // configurazione di visibilità/abilitazione propria, ma ereditano
          // quella della colonna di appartenenza + quella della riga.
          const cellColumn = columnMap.get(cell.columnId);
          if (!cellColumn) return [];
          return [
            {
              ...cell.widget,
              ...mergeConditions(cellColumn, row),
            },
          ];
        }),
      );
      continue;
    }

    for (const column of table.columns) {
      const materializedRowPrefix = materializeTableWidgetRowPrefix(
        table,
        row,
        r,
      );

      const cell = {
        ...column,
        ...mergeConditions(column, row),
      };

      // Ci sono dei riferimenti che devono essere normalizzati poiché
      // virtuali, ovvero che fanno riferimento alla propria riga.
      const virtualColumnReferences = findVirtualReferencesInWidget(cell);
      if (virtualColumnReferences.referencesPaths.length > 0) {
        // TODO Al momento facciamo side-effect sul widget stesso della colonna.
        // Essendo un loop sulle righe, in realtà si comporta come un `or`
        column.__hasVirtualColumnRefs = true;
      }

      widgets.push({
        ...materializeReferencesInColumn(
          cell,
          materializedRowPrefix,
          virtualColumnReferences,
        ),
        fromVirtualId: column.id,
        id: engineWidgetCellId(table, row.id, column.id),
        code: `${materializedRowPrefix}_${column.code}`,
      } as Widget & ICodeWidget);
    }
  }

  // console.log(`[materializeTableInputWidgets] widgets:`, widgets);
  return widgets;
}

function materializeReferencesInColumn(
  column: IBaseWidget,
  prefix: string,
  virtualPaths: IVirtualReferencePaths,
) {
  return produce(column, draft => {
    for (const targetPath of virtualPaths.referencesPaths) {
      debug.log('virtualrefs.normalize', `From virtual`, { targetPath });
      const virtualRef = get(column, targetPath) as IReference;
      const normalizedRef = normalizeVirtualReference(virtualRef, prefix);
      // biome-ignore format: <tbd>
      debug.log('virtualrefs.normalize', `From virtual`, { virtualRef, normalizedRef });
      set(draft, targetPath, normalizedRef);
    }

    for (const rtePath of virtualPaths.rtePaths) {
      const rte = get(column, rtePath) as IAdvancedRichText;
      set(
        draft,
        rtePath,
        rte.map(item => ({
          ...item,
          text: normalizeVirtualRefsRte(item.text, prefix),
        })),
      );
    }
  });
}
